import { useState, useEffect, useRef } from "react";
import {
    PrimaryButton,
    DefaultButton,
    TextField,
    Label,
    Checkbox,
    Stack,
    Text
} from "@fluentui/react";
import { Search24Regular, Dismiss24Regular, Eye24Regular, Open24Regular, Copy24Regular, Delete24Regular, FilterDismiss20Regular } from "@fluentui/react-icons";
import { DescriptionModal } from "../DescriptionModal";
import { formatStudyTitle } from "../../utils/formatters";
import { ActionButton } from "../ActionButton";
import styles from "./SearchModal.module.css";

interface SearchFilters {
    publicacion: string;
    subdireccion: string;
    categoria: string;
    ano: string;
}

interface SearchResult {
    id: number;
    title: string;
    year: string;
    subdireccion: string;
    categoria: string;
    selected: boolean;
    filepaths: string[];
    sharepoint?: string;
    description?: string;
    id_dnp?: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (selectedSources: SearchResult[]) => void;
    initialSelectedIds?: (number | string)[];
}

const subdirecciones = [
    { key: "", text: "Selecciona una dependencia" },
    { key: "Dirección de Desarrollo Digital", text: "Dirección de Desarrollo Digital" },
    { key: "Energía y Minas", text: "Energía y Minas" },
    { key: "Infraestructura y Transporte Nacional", text: "Infraestructura y Transporte Nacional" },
    { key: "Logística", text: "Logística" },
    { key: "Movilidad Urbana y Regional", text: "Movilidad Urbana y Regional" }
];

const categorias = [
    { key: "", text: "Selecciona una categoría" },
    { key: "Analítica de datos", text: "Analítica de datos" },
    { key: "Estudios del panorama regional", text: "Estudios del panorama regional" },
    { key: "Estudios específicos", text: "Estudios específicos" },
    { key: "Guías", text: "Guías" },
    { key: "Productos de consultores", text: "Productos de consultores" }
];

const years = [
    { key: "", text: "Selecciona un año" },
    { key: "2026", text: "2026" },
    { key: "2025", text: "2025" },
    { key: "2024", text: "2024" },
    { key: "2023", text: "2023" },
    { key: "2022", text: "2022" },
    { key: "2021", text: "2021" },
    { key: "2020", text: "2020" },
    { key: "2019", text: "2019" },
    { key: "2018", text: "2018" }
];

export const SearchModal = ({ isOpen, onClose, onSave, initialSelectedIds }: Props) => {
    const [filters, setFilters] = useState<SearchFilters>({
        publicacion: "",
        subdireccion: "",
        categoria: "",
        ano: ""
    });
    const [allDocuments, setAllDocuments] = useState<SearchResult[]>([]);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
    const [descriptionModal, setDescriptionModal] = useState<{
        isOpen: boolean;
        title: string;
        description: string;
        id_dnp: string;
    }>({ isOpen: false, title: "", description: "", id_dnp: "" });
    const [showCopyToast, setShowCopyToast] = useState(false);
    const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Cargar documentos desde el JSON al montar el componente
    useEffect(() => {
        const loadDocuments = async () => {
            try {
                const response = await fetch('/documentos.json');
                const data = await response.json();
                setAllDocuments(data);
                // Mostrar todos por defecto inicial
                setSearchResults(data.map((r: any) => ({
                    ...r,
                    selected: initialSelectedIds?.some(id => id === r.id || id === r.id_dnp) || false
                })));
                setHasSearched(true);
            } catch (error) {
                console.error('Error cargando documentos:', error);
                setAllDocuments([]);
            } finally {
                setIsLoadingDocuments(false);
            }
        };

        loadDocuments();
    }, []);

    // Sincronizar selectedIds con initialSelectedIds cuando el modal se abre
    useEffect(() => {
        if (isOpen && initialSelectedIds) {
            setSelectedIds(initialSelectedIds);
        }
    }, [isOpen, initialSelectedIds]);

    // Efecto para filtrado automático
    useEffect(() => {
        if (!isLoadingDocuments) {
            handleSearch();
        }
    }, [filters, allDocuments, selectedIds]); // Añadimos selectedIds para refrescar la vista al cambiar seleccion

    // Función para normalizar texto (quitar acentos y convertir a minúsculas)
    const normalizeText = (text: string): string => {
        return text
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
    };

    const handleSearch = async () => {
        setIsSearching(true);

        // Usar setTimeout pequeño para dar feedback visual de "Buscando..."
        const timer = setTimeout(() => {
            let results = [...allDocuments];

            // Aplicar filtros
            if (filters.publicacion) {
                const normalizedFilter = normalizeText(filters.publicacion);
                results = results.filter(r =>
                    normalizeText(r.title).includes(normalizedFilter)
                );
            }
            if (filters.subdireccion) {
                const selectedOption = subdirecciones.find(s => s.key === filters.subdireccion);
                if (selectedOption) {
                    results = results.filter(r => r.subdireccion === selectedOption.text);
                }
            }
            if (filters.categoria) {
                const selectedOption = categorias.find(c => c.key === filters.categoria);
                if (selectedOption) {
                    results = results.filter(r => r.categoria === selectedOption.text);
                }
            }
            if (filters.ano) {
                results = results.filter(r => r.year === filters.ano);
            }

            setSearchResults(results.map(r => ({
                ...r,
                selected: selectedIds.some(id => id === r.id || id === r.id_dnp)
            })));
            setHasSearched(true);
            setIsSearching(false);
        }, 150);

        return () => clearTimeout(timer);
    };

    const handleResultSelect = (result: SearchResult, checked: boolean) => {
        if (checked) {
            setSelectedIds(prev => [...prev, result.id]);
        } else {
            setSelectedIds(prev => prev.filter(curId => curId !== result.id && curId !== result.id_dnp));
        }
    };

    const toggleDescription = (title: string, description?: string, id_dnp?: string) => {
        if (description) {
            setDescriptionModal({
                isOpen: true,
                title: title,
                description: description,
                id_dnp: id_dnp || ""
            });
        }
    };

    const handleCopySharepointLink = async (url: string) => {
        try {

            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(url);
                setShowCopyToast(true);
            } else {
                // Fallback para iframes con restricciones
                copyToClipboardFallback(url);
                setShowCopyToast(true);
            }
        } catch (error) {
            console.error('Error al copiar:', error);
            copyToClipboardFallback(url);
            setShowCopyToast(true);
        }

        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        toastTimeoutRef.current = setTimeout(() => {
            setShowCopyToast(false);
        }, 2000);
    };

    const copyToClipboardFallback = (text: string) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Fallback: Error al copiar:', err);
        }
        document.body.removeChild(textarea);
    };

    const handleSave = () => {
        const selectedSourcesSelection = allDocuments.filter(r =>
            selectedIds.some(id => id === r.id || id === r.id_dnp)
        );
        onSave(selectedSourcesSelection);
        onClose();
        // Reset state
        setFilters({ publicacion: "", subdireccion: "", categoria: "", ano: "" });
        setSearchResults([]);
        setHasSearched(false);
    };

    const handleClose = () => {
        onClose();
        // Reset state
        setFilters({ publicacion: "", subdireccion: "", categoria: "", ano: "" });
        setSearchResults([]);
        setHasSearched(false);
    };

    const selectedCount = new Set(selectedIds).size;
    const isFiltered = filters.publicacion !== "" || filters.subdireccion !== "" || filters.categoria !== "" || filters.ano !== "";
    const statusLabel = isFiltered
        ? `publicaci${searchResults.length === 1 ? 'ón' : 'ones'} filtrada${searchResults.length === 1 ? '' : 's'}`
        : `publicaci${searchResults.length === 1 ? 'ón' : 'ones'} disponible${searchResults.length === 1 ? '' : 's'}`;

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // Pequeño timeout para asegurar que el DOM está listo
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <Search24Regular className={styles.titleIcon} />
                        <h2 className={styles.modalTitle}>Buscar publicaciones</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className={styles.closeButton}
                        aria-label="Cerrar"
                    >
                        <Dismiss24Regular />
                    </button>
                </div>

                <div className={styles.body}>
                    <div className={styles.stickyHeader}>
                        <div className={styles.filtersGrid}>
                            <div className={styles.filterItem}>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className={styles.nativeInput}
                                    value={filters.publicacion}
                                    onChange={(e) => setFilters(prev => ({ ...prev, publicacion: e.target.value }))}
                                    placeholder="Buscar aquí por nombre de la publicación"
                                    autoFocus
                                />
                            </div>

                            <div className={styles.filterItem}>
                                <select
                                    className={styles.nativeSelect}
                                    value={filters.subdireccion}
                                    onChange={(e) => setFilters(prev => ({ ...prev, subdireccion: e.target.value }))}
                                >
                                    {subdirecciones.map(o => <option key={o.key} value={o.key}>{o.text}</option>)}
                                </select>
                            </div>

                            <div className={styles.filterItem}>
                                <select
                                    className={styles.nativeSelect}
                                    value={filters.categoria}
                                    onChange={(e) => setFilters(prev => ({ ...prev, categoria: e.target.value }))}
                                >
                                    {categorias.map(o => <option key={o.key} value={o.key}>{o.text}</option>)}
                                </select>
                            </div>

                            <div className={styles.filterItem}>
                                <select
                                    className={styles.nativeSelect}
                                    value={filters.ano}
                                    onChange={(e) => setFilters(prev => ({ ...prev, ano: e.target.value }))}
                                >
                                    {years.map(o => <option key={o.key} value={o.key}>{o.text}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className={styles.searchStatus}>
                            <div className={styles.searchStatusText}>
                                {isLoadingDocuments ? "Cargando biblioteca..." : isSearching ? "Buscando..." : (
                                    <>
                                        <div className={styles.statusResultsInfo}>
                                            <span>{searchResults.length} {statusLabel}</span>
                                            {(filters.publicacion !== "" || filters.subdireccion !== "" || filters.categoria !== "" || filters.ano !== "") && (
                                                <button
                                                    className={styles.clearFiltersButton}
                                                    onClick={() => setFilters({ publicacion: "", subdireccion: "", categoria: "", ano: "" })}
                                                    title="Quitar todos los filtros"
                                                >
                                                    <FilterDismiss20Regular />
                                                </button>
                                            )}
                                        </div>
                                        <span className={styles.statusSeparator}>-</span>
                                        <span className={styles.selectedCountText}>
                                            {selectedCount === 0 ? (searchResults.length === 1 ? "Puedes seleccionarla" : "Selecciona una o más") : `${selectedCount} seleccionada${selectedCount !== 1 ? 's' : ''}`}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className={styles.topActions}>
                            <ActionButton variant="secondary" onClick={handleClose} className={styles.topActionButton}>
                                Cancelar
                            </ActionButton>
                            <ActionButton
                                variant="primary"
                                onClick={handleSave}
                                disabled={selectedCount === 0}
                                className={styles.topActionButton}
                            >
                                {/* Guardar ({selectedCount}) */}
                                Guardar Selección
                            </ActionButton>
                        </div>
                    </div>

                    {hasSearched && (
                        <div className={styles.resultsSection}>

                            <div className={styles.resultsList}>
                                {searchResults.length === 0 ? (
                                    <Text className={styles.noResults}>
                                        No se encontraron documentos con los filtros especificados.
                                    </Text>
                                ) : (
                                    searchResults.map(result => (
                                        <div key={result.id} className={styles.resultItem}>
                                            <div className={styles.resultHeader}>
                                                <Checkbox
                                                    checked={result.selected}
                                                    onChange={(_, checked) => handleResultSelect(result, checked || false)}
                                                />
                                                <div className={styles.resultActions}>
                                                    {result.description && (
                                                        <button
                                                            className={styles.descriptionToggle}
                                                            onClick={() => toggleDescription(result.title, result.description, result.id_dnp)}
                                                            title="Ver descripción"
                                                            aria-label="Ver descripción"
                                                        >
                                                            <Eye24Regular />
                                                        </button>
                                                    )}
                                                    {result.sharepoint && (
                                                        <>
                                                            <button
                                                                className={styles.descriptionToggle}
                                                                onClick={() => window.open(result.sharepoint)}
                                                                title="Ver documentos asociados"
                                                                aria-label="Ver documentos asociados"
                                                            >
                                                                <Open24Regular />
                                                            </button>
                                                            <button
                                                                className={styles.descriptionToggle}
                                                                onClick={() => handleCopySharepointLink(result.sharepoint || '')}
                                                                title="Copiar enlace de documentos asociados"
                                                                aria-label="Copiar enlace de documentos asociados"
                                                            >
                                                                <Copy24Regular />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={styles.resultInfo}>
                                                <div className={styles.resultTitle}>
                                                    {formatStudyTitle(result.title)}
                                                    <span className={styles.resultYearInline}>{result.year}</span>
                                                </div>
                                                <div className={styles.resultFullDetails}>
                                                    <span className={styles.resultYearLabel}>{result.year}</span>
                                                    <Text variant="small">
                                                        • {result.subdireccion} • {result.categoria}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {showCopyToast && (
                    <div className={styles.toast}>
                        ✓ Enlace copiado
                    </div>
                )}
            </div>

            <DescriptionModal
                isOpen={descriptionModal.isOpen}
                onClose={() => setDescriptionModal({ isOpen: false, title: "", description: "", id_dnp: "" })}
                title={descriptionModal.title}
                description={descriptionModal.description}
                id_dnp={descriptionModal.id_dnp}
            />
        </div>
    );
};

